import type {Api, SdkConfig} from '@/modules/common/context/Bloomreach/models';

import {pick} from 'lodash';
import {useCallback, useMemo, useRef, useState} from 'react';

import {BloomreachContext} from '@/modules/common/context/Bloomreach/BloomreachContext';
import {getSdkIntegrationSnippet} from '@/modules/common/context/Bloomreach/bloomreachSdkSnippet';
import {SDK_SNIPPET_CONFIG_KEYS} from '@/modules/common/context/Bloomreach/models';

declare const window: {
  exponea: Api;
} & Window;

type Props = React.PropsWithChildren;

export const BloomreachProvider: React.FunctionComponent<Props> = ({
  children,
}) => {
  const [sdkInstance, setSdkInstance] = useState<Api | null>(null);
  const sdkInitialized = useRef(false);

  const initSdkOnce = useCallback((sdkConfig: SdkConfig) => {
    // Init only once
    if (sdkInitialized.current === true) {
      return;
    }
    sdkInitialized.current = true;
    // This is a bit weird, bloomreach specifies just one sdk config object
    // but some of it's fields should only be passed to the integration snippet.
    // The majority can be passed to the "start"-method.
    // @see https://documentation.bloomreach.com/engagement/docs/integration#configuration
    const snippetConfig = pick(sdkConfig, SDK_SNIPPET_CONFIG_KEYS);
    // Add SDK
    const script = document.createElement('script');
    script.id = 'bloomreach-sdk-snippet'; // for debugging
    script.text = getSdkIntegrationSnippet(snippetConfig);
    document.head.append(script);
    // The SDK methods are available immediately after the script has been added,
    // because it provides a command queue for each of them.
    // So we can start calling things right away once the sdkInstance is available.
    const exponeaSDKInstance = window.exponea;
    setSdkInstance(exponeaSDKInstance);
  }, []);

  const contextValue = useMemo(
    () => ({
      initSdkOnce,
      api: sdkInstance,
    }),
    [initSdkOnce, sdkInstance]
  );

  return (
    <BloomreachContext.Provider value={contextValue}>
      {children}
    </BloomreachContext.Provider>
  );
};
